"use client";

import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import type { HeroProduct } from "@/app/page";

// A single parallax tile (x4 in the source). Reveals its lot economics when
// it is the featured tile.
function Tile({
  product,
  featured,
  mouseX,
  mouseY,
}: {
  product: HeroProduct;
  featured: boolean;
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
}) {
  const x = useTransform(
    mouseX,
    [-0.5, 0.5],
    [-product.depth * 14, product.depth * 14],
  );
  const y = useTransform(
    mouseY,
    [-0.5, 0.5],
    [-product.depth * 14, product.depth * 14],
  );

  const color =
    product.status === "live"
      ? "#22c55e"
      : product.status === "upcoming"
      ? "#3b82f6"
      : "#6b7280";
  const label =
    product.status === "live"
      ? "LIVE"
      : product.status === "upcoming"
      ? "UPCOMING"
      : "SOLD";
  const margin =
    product.price > 0
      ? Math.round(((product.retail - product.price) / product.price) * 100)
      : 0;

  // Live tiles carry the listing id, so they deep-link into the feed and scroll
  // to that lot's card. Non-live showcase tiles (upcoming/sold) are not links.
  const href = product.id ? `/feed#lot-${product.id}` : undefined;
  const Wrapper = href ? motion.a : motion.div;

  return (
    <Wrapper
      {...(href ? { href } : {})}
      style={{
        x,
        y,
        top: product.pos.top,
        left: product.pos.left,
        zIndex: featured ? 50 : Math.round(product.depth * 10),
      }}
      className={`absolute ${href ? "cursor-pointer" : ""}`}
      animate={{ scale: featured ? 1.06 : 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      <div
        className={`relative w-28 h-40 sm:w-36 sm:h-52 lg:w-44 lg:h-64 rounded-2xl overflow-hidden shadow-2xl ${
          featured ? "ring-2 ring-blue-500/50" : ""
        }`}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={product.img}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/5" />
        <span
          className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[8px] sm:text-[9px] font-bold text-white"
          style={{ backgroundColor: color }}
        >
          {label}
        </span>
        <div className="absolute bottom-0 left-0 right-0 p-2.5">
          <p className="text-[8px] sm:text-[9px] text-white/50 uppercase tracking-wider mb-0.5">
            {product.category}
          </p>
          <p className="text-[11px] sm:text-xs text-white font-semibold leading-tight">
            {product.name}
          </p>
          <AnimatePresence>
            {featured && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 pt-2 border-t border-white/15 space-y-0.5 text-[9px] sm:text-[10px]">
                  <div className="flex justify-between">
                    <span className="text-white/50">Units</span>
                    <span className="text-white font-semibold">
                      {product.units}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Retail</span>
                    <span className="text-white font-semibold line-through opacity-70">
                      ${product.retail}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Member</span>
                    <span className="text-blue-400 font-semibold">
                      ${product.price}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Margin</span>
                    <span className="text-green-400 font-semibold">
                      {margin}%
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Wrapper>
  );
}

export function HeroCollage({
  products,
  authenticated,
}: {
  products: HeroProduct[];
  authenticated: boolean;
}) {
  const [featured, setFeatured] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const springX = useSpring(mx, { stiffness: 40, damping: 25 });
  const springY = useSpring(my, { stiffness: 40, damping: 25 });

  useEffect(() => {
    const id = setInterval(
      () => setFeatured((g) => (g + 1) % products.length),
      4000,
    );
    return () => clearInterval(id);
  }, [products.length]);

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section className="relative min-h-screen flex items-center pt-24 lg:pt-0 overflow-hidden bg-[#080808]">
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-900/15 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-900/10 rounded-full blur-[120px]" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-4 lg:px-8 grid lg:grid-cols-2 gap-8 items-center pt-0 pb-12 lg:py-0">
        <div className="flex flex-col justify-center max-w-xl order-2 lg:order-1">
          <p className="text-xs font-medium text-blue-400 tracking-[0.2em] uppercase mb-5">
            Private Wholesale Access
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6 tracking-tight">
            Inventory moves
            <br />
            differently here.
          </h1>
          <p className="text-lg text-gray-400 mb-8 leading-relaxed">
            Corporate & direct supply inventory, at cost,
            <br />
            no middleman, no markup.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <a
              href="/feed"
              className="flex items-center justify-center px-7 py-3.5 bg-white text-black font-semibold text-sm rounded-full hover:bg-gray-100 transition-colors"
            >
              View Live Inventory
            </a>
            {!authenticated && (
              <a
                href="/login"
                className="px-7 py-3.5 border border-white/15 text-white font-semibold text-sm rounded-full hover:bg-white/5 transition-colors text-center"
              >
                Login
              </a>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Built for resellers, retailers and wholesale operators.
          </p>
        </div>
        <div
          ref={ref}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          className="relative h-[360px] sm:h-[460px] lg:h-[560px] order-1 lg:order-2"
        >
          {products.map((p, i) => (
            <Tile
              key={i}
              product={p}
              featured={featured === i}
              mouseX={springX}
              mouseY={springY}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
